import styles from './ResumePaper.module.css'

interface ExperienceItem {
  company: string
  role: string
  period: string
  bullets: string[]
}

interface ProjectItem {
  name: string
  role: string
  period?: string
  bullets: string[]
}

interface SkillCategory {
  name: string
  items: string[]
}

interface EducationItem {
  school: string
  degree: string
  period: string
}

interface ResearchItem {
  title: string
  venue: string
  year: string
}

export interface ResumePaperContent {
  summary?: string
  coreCompetencies?: string[]
  experience?: ExperienceItem[]
  projects?: ProjectItem[]
  skills?: { categories?: SkillCategory[] }
  education?: EducationItem[]
  certifications?: string[]
  research?: ResearchItem[]
}

export interface ResumePaperProps {
  content: ResumePaperContent
}

/**
 * Pure presentation component: renders resume content as a document-style
 * paper. No data fetching, no API calls. All colors via CSS-variable tokens.
 */
export default function ResumePaper({ content }: ResumePaperProps) {
  const {
    summary,
    coreCompetencies,
    experience,
    projects,
    skills,
    education,
    certifications,
    research,
  } = content

  const skillCategories = skills?.categories

  return (
    <article className={styles.resumePaper}>
      {summary && (
        <section id="section-summary" className={styles.paperSection}>
          <h2 className={styles.sectionTitle}>Summary</h2>
          <p className={styles.summary}>{summary}</p>
        </section>
      )}

      {coreCompetencies && coreCompetencies.length > 0 && (
        <section id="section-competencies" className={styles.paperSection}>
          <h2 className={styles.sectionTitle}>Core Competencies</h2>
          <div className={styles.competencyTags}>
            {coreCompetencies.map((c, i) => (
              <span key={`${c}-${i}`} className={styles.competencyTag}>
                {c}
              </span>
            ))}
          </div>
        </section>
      )}

      {experience && experience.length > 0 && (
        <section id="section-experience" className={styles.paperSection}>
          <h2 className={styles.sectionTitle}>Experience</h2>
          {experience.map((exp, i) => (
            <div key={`${exp.company}-${i}`} className={styles.experienceItem}>
              <div className={styles.itemHeader}>
                <span className={styles.itemRole}>{exp.role}</span>
                <span className={styles.itemPeriod}>{exp.period}</span>
              </div>
              <div className={styles.itemCompany}>{exp.company}</div>
              <ul className={styles.bullets}>
                {exp.bullets.map((b, j) => (
                  <li key={`${i}-${j}`}>{b}</li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      )}

      {projects && projects.length > 0 && (
        <section id="section-projects" className={styles.paperSection}>
          <h2 className={styles.sectionTitle}>Projects</h2>
          {projects.map((p, i) => (
            <div key={`${p.name}-${i}`} className={styles.projectItem}>
              <div className={styles.itemHeader}>
                <span className={styles.itemRole}>{p.name}</span>
                <span className={styles.itemPeriod}>
                  {p.period ? `${p.role} · ${p.period}` : p.role}
                </span>
              </div>
              <ul className={styles.bullets}>
                {p.bullets.map((b, j) => (
                  <li key={`${i}-${j}`}>{b}</li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      )}

      {skillCategories && skillCategories.length > 0 && (
        <section id="section-skills" className={styles.paperSection}>
          <h2 className={styles.sectionTitle}>Skills</h2>
          {skillCategories.map((cat, i) => (
            <div key={`${cat.name}-${i}`} className={styles.skillGroup}>
              <div className={styles.skillGroupName}>{cat.name}</div>
              <div className={styles.skillGroupItems}>
                {cat.items.map((s, j) => (
                  <span key={`${s}-${j}`} className={styles.skillTag}>
                    {s}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </section>
      )}

      {education && education.length > 0 && (
        <section id="section-education" className={styles.paperSection}>
          <h2 className={styles.sectionTitle}>Education</h2>
          {education.map((edu, i) => (
            <div key={`${edu.school}-${i}`} className={styles.experienceItem}>
              <div className={styles.itemHeader}>
                <span className={styles.itemRole}>{edu.school}</span>
                <span className={styles.itemPeriod}>{edu.period}</span>
              </div>
              <div className={styles.itemCompany}>{edu.degree}</div>
            </div>
          ))}
        </section>
      )}

      {certifications && certifications.length > 0 && (
        <section className={styles.paperSection}>
          <h2 className={styles.sectionTitle}>Certifications</h2>
          <ul className={styles.bullets}>
            {certifications.map((c, i) => (
              <li key={`${c}-${i}`}>{c}</li>
            ))}
          </ul>
        </section>
      )}

      {research && research.length > 0 && (
        <section className={styles.paperSection}>
          <h2 className={styles.sectionTitle}>Research</h2>
          <ul className={styles.bullets}>
            {research.map((r, i) => (
              <li key={`${r.title}-${i}`}>
                <strong className={styles.researchTitle}>{r.title}</strong>
                <span className={styles.researchMeta}>
                  {' '}
                  · {r.venue} ({r.year})
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </article>
  )
}
